import { create } from 'zustand';
import {
  AssistantThinkingState,
  ConversationMessageAudience,
} from '../types/conversation.types';

/**
 * Bridge between the open thread and the socket.
 *
 * The socket itself is mounted once in `AdminLayout`, so the unread badge stays
 * live on every screen rather than only on the chat page. That leaves the
 * thread with no handle on it, and this store is how the two talk: the thread
 * publishes which conversation it is showing, and the socket publishes what it
 * hears back.
 *
 * Deliberately NOT part of `useAuth`. That store is selected whole
 * (`useAuth((state) => state)`) inside an effect that can assign
 * `window.location.href`, so any chat write into it would turn a busy inbox
 * into a navigation storm.
 */

export type TypingUser = {
  userId: number;
  name: string;
};

type ChatSocketState = {
  /**
   * The conversation currently on screen, or null. The socket joins its room
   * and uses it to decide whether an incoming message is already being read.
   */
  observedConversationId: number | string | null;
  setObservedConversationId: (id: number | string | null) => void;

  /**
   * Set when the inbox can no longer be trusted to match the server: a socket
   * event that could not be applied safely (see the keyset-cursor note in
   * `useConversationsSocket`), or the tab turning visible with a dead socket.
   * Rather than reorder rows the already-issued cursors were derived from,
   * the writer says "this list is behind" and leaves it to the inbox, which
   * refetches itself.
   */
  inboxStale: boolean;
  setInboxStale: (stale: boolean) => void;

  /**
   * The query key the inbox is showing right now, published by the page.
   *
   * The socket needs it for two reasons. It has to write into the list the user
   * is actually looking at - each filter combination caches separately, so
   * updating only the unfiltered key would leave a filtered inbox frozen. And
   * when it is null the chat page is not mounted at all, which means there is
   * nothing on screen to keep in sync and nothing to flag as stale.
   */
  activeInboxKey: readonly unknown[] | null;
  setActiveInboxKey: (key: readonly unknown[] | null) => void;

  /**
   * Which tab that list is - `merchant`, `sales` or the internal pair.
   *
   * Published beside the key rather than read out of it: the socket has to know
   * whether an incoming row even belongs on screen, and answering that by
   * indexing into the key would tie the two files together by tuple position.
   */
  activeInboxScope: string | null;
  setActiveInboxScope: (scope: string | null) => void;

  /**
   * Registered by the socket hook, called by the composer. Null until the
   * socket connects, and a no-op on the merchant side, which has no typing
   * channel.
   */
  /* `audience` rides along in a dispatcher thread, so typing a note stays
     private - the customer only sees `everyone` typing. */
  emitTyping:
    | ((
        conversationId: number | string,
        typing: boolean,
        audience?: ConversationMessageAudience
      ) => void)
    | null;
  setEmitTyping: (
    fn:
      | ((
          conversationId: number | string,
          typing: boolean,
          audience?: ConversationMessageAudience
        ) => void)
      | null
  ) => void;

  /** Who is typing in the observed conversation right now. */
  typingUsers: TypingUser[];
  setTypingUsers: (users: TypingUser[]) => void;

  /**
   * What the AI assistant is doing in the observed conversation, or null when
   * it is doing nothing. The socket hook owns the timers that set and clear
   * this - the ~400ms wait before it appears and the safety net that drops it
   * if `done` never arrives - so a reader only has to render what is here.
   */
  assistantState: AssistantThinkingState | null;
  setAssistantState: (state: AssistantThinkingState | null) => void;
};

export const useChatSocketState = create<ChatSocketState>()((set) => ({
  observedConversationId: null,
  setObservedConversationId: (id) =>
    set({ observedConversationId: id, typingUsers: [], assistantState: null }),

  inboxStale: false,
  setInboxStale: (stale) => set({ inboxStale: stale }),

  activeInboxKey: null,
  setActiveInboxKey: (key) => set({ activeInboxKey: key }),

  activeInboxScope: null,
  setActiveInboxScope: (scope) => set({ activeInboxScope: scope }),

  emitTyping: null,
  setEmitTyping: (fn) => set({ emitTyping: fn }),

  typingUsers: [],
  setTypingUsers: (users) => set({ typingUsers: users }),

  assistantState: null,
  setAssistantState: (state) => set({ assistantState: state }),
}));
